import { PdfBlock, PdfColor, PdfItem, createPdfBlob } from '../../core/pdf/pdf-document';
import { QuestionarioFormValue } from '../../core/models/questionario-form.model';

/**
 * Converte o snapshot do formulário no documento PDF do botão "Exportar".
 *
 * O layout espelha o do questionário na tela: enunciado numerado e cada alternativa em uma
 * caixa arredondada com marcador circular à esquerda. A ordem das perguntas e das alternativas
 * é a mesma do formulário, e cada pergunta vira um bloco `keepTogether` para não se separar
 * das suas alternativas na quebra de página.
 */

const CORPO = 10;
const TITULO_DOCUMENTO = 17;
const TITULO_SECAO = 12;

/** Paleta em tons de cinza, no mesmo espírito do layout da tela. */
const TINTA_FORTE: PdfColor = [0.1, 0.1, 0.1];
const TINTA_MEDIA: PdfColor = [0.35, 0.35, 0.35];
const TINTA_SUAVE: PdfColor = [0.5, 0.5, 0.5];
const DIVISORIA_FORTE: PdfColor = [0.8, 0.8, 0.8];
const DIVISORIA_SUAVE: PdfColor = [0.898, 0.898, 0.898];

function titulo(text: string, size: number, spacingBefore: number): PdfItem {
  return {
    kind: 'text',
    run: { text, font: 'bold', size, color: TINTA_FORTE },
    spacingBefore,
    spacingAfter: 6,
  };
}

function linha(text: string, color: PdfColor = TINTA_MEDIA): PdfItem {
  return { kind: 'text', run: { text, size: CORPO, color } };
}

function informacoesGerais(questionario: QuestionarioFormValue): PdfBlock {
  return {
    keepTogether: true,
    items: [
      titulo('1. Informações gerais', TITULO_SECAO, 18),
      linha(`Tipo de Questionário: ${questionario.tipo}`),
      linha(`Nome: ${questionario.nome}`),
    ],
  };
}

function perguntas(questionario: QuestionarioFormValue): PdfBlock[] {
  const cabecalho: PdfBlock = { items: [titulo('2. Perguntas', TITULO_SECAO, 22)] };

  if (questionario.perguntas.length === 0) {
    return [cabecalho, { items: [linha('Nenhuma pergunta adicionada.', TINTA_SUAVE)] }];
  }

  const blocos = questionario.perguntas.map<PdfBlock>((pergunta, indice) => ({
    // É este flag que impede o enunciado de ficar no pé de uma página e as alternativas na
    // seguinte.
    keepTogether: true,
    items: [
      {
        kind: 'text',
        // Mesma fonte, corpo e cor do nome do perfil na tabela da seção 3.
        run: { text: `${indice + 1}. ${pergunta.texto}`, size: CORPO, color: TINTA_MEDIA },
        spacingBefore: indice === 0 ? 6 : 20,
        spacingAfter: 4,
      },
      ...pergunta.alternativas.map<PdfItem>((alternativa) => ({
        kind: 'option',
        run: { text: alternativa.texto, size: CORPO, color: TINTA_SUAVE },
        trailing: { text: `${alternativa.pontos} pts`, size: CORPO - 1, color: TINTA_SUAVE },
      })),
    ],
  }));

  return [cabecalho, ...blocos];
}

/** Início de cada coluna da tabela de perfis, como fração da largura útil. */
const COLUNAS_PERFIL = [0, 0.45, 0.72];
const CABECALHO_PERFIL = ['Perfil', 'Pontuação mínima', 'Pontuação máxima'];

const SEM_PONTUACAO = '-';

function linhaTabela(
  celulas: readonly string[],
  color: PdfColor,
  dividerColor: PdfColor,
): PdfItem {
  return {
    kind: 'row',
    cells: celulas.map((text) => ({ text, size: CORPO, color })),
    columnStarts: COLUNAS_PERFIL,
    dividerColor,
  };
}

/**
 * Diferente das alternativas, os perfis saem em tabela — as caixas com marcador circular são a
 * linguagem visual de "opção a escolher" e não fazem sentido aqui.
 */
function perfis(questionario: QuestionarioFormValue): PdfBlock[] {
  const cabecalhoSecao: PdfBlock = { items: [titulo('3. Associe o perfil', TITULO_SECAO, 26)] };

  if (questionario.perfis.length === 0) {
    return [cabecalhoSecao, { items: [linha('Nenhum perfil associado.', TINTA_SUAVE)] }];
  }

  const linhas = questionario.perfis.map((perfil) =>
    linhaTabela(
      [
        perfil.nomePerfil,
        `${perfil.pontuacaoMinima ?? SEM_PONTUACAO}`,
        `${perfil.pontuacaoMaxima ?? SEM_PONTUACAO}`,
      ],
      TINTA_MEDIA,
      DIVISORIA_SUAVE,
    ),
  );

  return [
    cabecalhoSecao,
    {
      keepTogether: true,
      items: [linhaTabela(CABECALHO_PERFIL, TINTA_SUAVE, DIVISORIA_FORTE), ...linhas],
    },
  ];
}

/** Documento completo, pronto para download. */
export function createQuestionarioPdfBlob(questionario: QuestionarioFormValue): Blob {
  const nome = questionario.nome.trim() || 'Questionário';

  const blocos: PdfBlock[] = [
    {
      keepTogether: true,
      items: [
        {
          kind: 'text',
          run: { text: nome, font: 'bold', size: TITULO_DOCUMENTO, color: TINTA_FORTE },
          spacingAfter: 2,
        },
      ],
    },
    informacoesGerais(questionario),
    ...perguntas(questionario),
    ...perfis(questionario),
  ];

  return createPdfBlob(blocos, nome);
}

const NOME_ARQUIVO_PADRAO = 'questionario';

/** Minúsculas sem acento, com hífens no lugar de espaços e pontuação. */
function normalizarParaNomeArquivo(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** `toISOString()` converte para UTC e viraria o dia à noite, por isso as partes locais. */
function formatarDataArquivo(data: Date): string {
  const mes = `${data.getMonth() + 1}`.padStart(2, '0');
  const dia = `${data.getDate()}`.padStart(2, '0');

  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** Nome do arquivo baixado: `<nome>-<data>.pdf`, sem acentos nem pontuação. */
export function createQuestionarioPdfFileName(
  questionario: QuestionarioFormValue,
  geradoEm: Date = new Date(),
): string {
  const nome = normalizarParaNomeArquivo(questionario.nome) || NOME_ARQUIVO_PADRAO;

  return `${nome}-${formatarDataArquivo(geradoEm)}.pdf`;
}
